import React, {useCallback, useEffect, useMemo, useReducer, useRef, useState} from "react"
import {useLocation, useNavigate} from "react-router-dom"
import {Box} from "@mui/material"
import SideBarLayout from "src/v2/layout/side-bar-layout"
import FormHeader from "../components/FormHeader"
import { ReactComponent as WarningIcon } from "src/v2/assets/icons/warning-error-icon.svg"
import { ReactComponent as CheckIcon } from "src/v2/assets/icons/check-icon.svg"
import MotorService from "../../../../services/motor-service"
import ValidacaoService from "../../../../services/motor-validacao-service"
import { dispatchSnackbar } from "../../../components/SnackBar/snackbar-bus"
import TabsWrappedLabel from "../../../components/tabs/tabs-component"
import ParametrosPage from "./Parametros/parametros-page"
import EventosOrdinariosPage from "./EventosOrdinarios/eventos-ordinarios-page"
import EventosExtraordinariosPage from "./EventosExtraordinarios/eventos-extraordinarios-page"
import ValidationPage from "./Validacao/validation-page"
import useValidationData from "./Validacao/hooks/use-validation-data"
import { MODULOS_CADASTRO_EVENTOS } from "./cadastro-motor-eventos-const"
import MotorJurosService from "../../../../services/motor-juros-service"
import {MOTOR_EVENTOS_PAGE} from "src/utils/routes"
import {VALIDATION_MEMORY_COLUMNS} from "./Validacao/validation-memory"
import {getDecimalValidationMessage} from "./Validacao/validation-utils"
import {parseDate} from "src/v2/utils/validate-date/parse-date"
import {mapValidationCalculation} from "./Validacao/validation-preview"
import {
    createInitialValidationState,
    mapValidationSnapshot,
    validationSnapshotReducer,
} from "./Validacao/validation-snapshot"

const HEADER_DEFAULT = {
    title: `CETIP - | VER -`,
    statusIcon: <WarningIcon />,
    statusColor: "Em Andamento",
    statusLabel: "white",
}

export const MotorCadastroEventosPage = () => {
    const location = useLocation()
    const navigate = useNavigate()
    const isInitialMount = useRef(true)
    const jurosService = useMemo(() => new MotorJurosService(), [])
    const state = location.state
    const cadastroId = state?.RendaFixaCaracteristicasID
    const hasCadastroContext = cadastroId !== null && cadastroId !== undefined
    const idCaracteristica = cadastroId
    const cetip = state?.CETIP ?? null
    const versaoDocumento = state?.VersaoDocumento ?? null
    const calculationIdentity = JSON.stringify([cadastroId, cetip, versaoDocumento])
    const pageTitle = "Cadastro de Eventos"
    const [header, setHeader] = useState(HEADER_DEFAULT)
    const [caracteristicaCarregada, setCaracteristicaCarregada] = useState(null)
    const caracteristica = caracteristicaCarregada?.identity === calculationIdentity
        ? caracteristicaCarregada.data
        : null
    const calculationIdentityRef = useRef(calculationIdentity)
    calculationIdentityRef.current = calculationIdentity
    const [loadingState, setLoadingState] = useState({identity: calculationIdentity, sources: {}})
    const setSourceLoading = useCallback((source, value) => {
        if (calculationIdentityRef.current !== calculationIdentity) return
        setLoadingState((current) => ({
            identity: calculationIdentity,
            sources: {
                ...(current.identity === calculationIdentity ? current.sources : {}),
                [source]: value,
            },
        }))
    }, [calculationIdentity])
    const loadingCallbacks = useMemo(() => Object.fromEntries(
        ["parametros", "ordinarios", "extraordinarios"]
            .map((source) => [source, (value) => setSourceLoading(source, value)]),
    ), [setSourceLoading])
    const calculationSourceStatuses = loadingState.identity === calculationIdentity
        ? Object.entries(loadingState.sources)
            .filter(([source]) => source !== "premio" && source !== "conversibilidade")
            .map(([, status]) => status)
        : []
    const loading = calculationSourceStatuses.includes(true)
    const draftLoadError = calculationSourceStatuses.includes("error")
    const motorService = useMemo(() => new MotorService(), [])
    const validacaoService = useMemo(() => new ValidacaoService(), [])
    const calculationPendingRef = useRef(null)
    const calculationRevisionRef = useRef(0)
    const calculationMountedRef = useRef(true)
    const initialTab = state?.initialTab || MODULOS_CADASTRO_EVENTOS.PARAMETROS
    const [tabValue, setTabValue] = useState(initialTab)
    const [jurosOptions, setJurosOptions] = useState([])
    const [parametrosProducao, setParametrosProducao] = useState(false)
    const [eventosProducao, setEventosProducao] = useState(false)
    const [eventosExtraordinariosProducao, setEventosExtraordinariosProducao] = useState(false)
    const [eventosProjetados, setEventosProjetados] = useState(null)
    const [recompraProgramadaProjetados, setRecompraProgramadaProjetados] = useState(null)
    const [datasAniversarioProjetadas, setDatasAniversarioProjetadas] = useState(null)
    const [validationState, dispatchValidationState] = useReducer(
        validationSnapshotReducer,
        undefined,
        createInitialValidationState,
    )
    const validationDraftRef = useRef(validationState.snapshot)
    validationDraftRef.current = validationState.snapshot

    const updateValidationDraft = useCallback((draft) => {
        const nextDraft = {...validationDraftRef.current, ...draft}
        if (JSON.stringify(nextDraft) === JSON.stringify(validationDraftRef.current)) return
        validationDraftRef.current = nextDraft
        calculationRevisionRef.current += 1
        dispatchValidationState({type: "HYDRATE_DRAFT", draft})
    }, [])

    const markValidationDraftEdited = useCallback(() => {
        calculationRevisionRef.current += 1
        dispatchValidationState({type: "UPDATE_DRAFT"})
    }, [])

    const invalidateValidationResults = useCallback(() => {
        calculationRevisionRef.current += 1
        dispatchValidationState({type: "INVALIDATE_RESULTS"})
    }, [])

    const loadCaracteristicas = useCallback((RendaFixaCaracteristicasID, identity, isRequestCurrent) => {
        motorService.getCadastro(RendaFixaCaracteristicasID)
            .then((response) => {
                if (!isRequestCurrent()) return

                const {body} = response
                setCaracteristicaCarregada({identity, data: body})
            })
            .catch(() => {
                if (!isRequestCurrent()) return

                dispatchSnackbar("Não foi possível carregar os dados de cadastro", "error")
            })
    }, [motorService])

    const {
        parameters: validationParameters,
        setParameters: setValidationParameters,
        savedViewModel,
        isLoadingValidation,
        referenceDateError,
        saveEmission,
        isSavingEmission,
        saveValidation,
        isSavingValidation,
        exportExcel,
    } = useValidationData({
        cadastroId,
        motorService,
        onEmissionSaved: (updates) => setCaracteristicaCarregada((current) => {
            if (current?.identity !== calculationIdentity) return current
            const data = current.data.Caracteristica
                ? {...current.data, Caracteristica: {...current.data.Caracteristica, ...updates}}
                : {...current.data, ...updates}
            return {...current, data}
        }),
        onSnapshotChange: invalidateValidationResults,
        snapshotRevision: validationState.snapshotRevision,
    })

    const validationSnapshot = useMemo(() => mapValidationSnapshot(
        validationState.snapshot,
        {
            cetip,
            versaoDocumento,
            idCaracteristica,
        },
        validationParameters,
    ), [
        cetip,
        idCaracteristica,
        validationParameters,
        validationState.snapshot,
        versaoDocumento,
    ])

    const readyToCalculate = String(validationSnapshot.IDCaracteristica) === String(cadastroId)
        && Boolean(caracteristica)
        && Boolean(validationSnapshot.Caracteristica?.CETIP)
        && validationSnapshot.Caracteristica?.VersaoDocumento != null
        && !loading
        && !draftLoadError
        && !isLoadingValidation

    calculationRevisionRef.current = Math.max(calculationRevisionRef.current, validationState.snapshotRevision)

    const handleCalculate = async ({snapshot}) => {
        if (!readyToCalculate) return
        if (!snapshot.EventosOrdinarios.length) {
            dispatchSnackbar("Não há eventos ordinários para calcular. Confira a aba Eventos Ordinários.", "warning")
            return
        }
        const {DataRentabilidadeInicio, ValorNominal} = snapshot.Parametros
        if (!parseDate(DataRentabilidadeInicio?.split("-").reverse().join("/"))) {
            dispatchSnackbar("Informe uma Data de Início da Rentabilidade válida na aba Parâmetros.", "warning")
            return
        }
        if (getDecimalValidationMessage(ValorNominal) || !(Number(ValorNominal) > 0)) {
            dispatchSnackbar("Informe um Valor Nominal maior que zero na aba Parâmetros.", "warning")
            return
        }
        const revision = calculationRevisionRef.current
        const identity = calculationIdentity
        if (calculationPendingRef.current === revision) return
        const isCurrentCalculation = () => calculationMountedRef.current
            && calculationIdentityRef.current === identity
            && calculationRevisionRef.current === revision
            && calculationPendingRef.current === revision
        calculationPendingRef.current = revision
        dispatchValidationState({type: "CALCULATION_LOADING", identity})
        try {
            const response = await validacaoService.calcularValidacao(snapshot)
            if (!isCurrentCalculation()) return
            if (!response) {
                dispatchValidationState({type: "CALCULATION_ERROR", identity, revision})
                return
            }
            dispatchValidationState({type: "CALCULATION_SUCCESS", identity, revision, viewModel: mapValidationCalculation(response.body.Data)})
        } catch (error) {
            if (!isCurrentCalculation()) return
            dispatchValidationState({type: "CALCULATION_ERROR", identity, revision})
            const status = error.status ?? error.response?.status
            if (status === 401 || status === 403) return
            const body = error.response?.body
            const fallback = status === 400 || status === 422
                ? "Confira os dados informados e tente calcular novamente."
                : "Não foi possível calcular a validação."
            const message = typeof body?.Message === "string" && body.Message.trim()
                ? body.Message.trim()
                : fallback
            dispatchSnackbar(message, "error")
        } finally {
            if (calculationPendingRef.current === revision) calculationPendingRef.current = null
        }
    }

    useEffect(() => {
        calculationMountedRef.current = true
        return () => { calculationMountedRef.current = false }
    }, [])

    const loadJuros = useCallback(() => {
        jurosService.getCadastroJuros()
            .then((response) => {
                const { body: { results } } = response

                const listJuros = results.map(item => ({
                    IDJuros: item.IDJuros,
                    Calculo: item.Calculo,
                    CalculoDias: item.CalculoDias,
                }))

                setJurosOptions(listJuros || [])
            })
            .catch(() => {
                console.error("Erro ao carregar opções de juros")
            })
    }, [jurosService])

    const setCadastroProducao = useCallback((producao) => {
        if (producao) {
            setHeader((currentHeader) => ({
                ...currentHeader,
                statusLabel: "EM PRODUÇÃO",
                statusIcon: <CheckIcon />
            }))
        }
    }, [])

    const projetarEventos = (eventos, datasAniversario, recompraProgramada) => {
        setEventosProjetados(eventos)
        setDatasAniversarioProjetadas(datasAniversario)
        setRecompraProgramadaProjetados(recompraProgramada)
        setTabValue(MODULOS_CADASTRO_EVENTOS.EVENTOS_ORDINARIOS)
    }

    const parametrosComponent = <ParametrosPage
        producao={parametrosProducao}
        setProducao={setParametrosProducao}
        idCaracteristica={idCaracteristica}
        loading={loading}
        eventosState={state}
        jurosOptions={jurosOptions}
        setLoading={loadingCallbacks.parametros}
        projetarEventos={projetarEventos}
        caracteristica={caracteristica}
        onDraftChange={updateValidationDraft}
        onDraftEdit={markValidationDraftEdited}
    />

    const eventosOrdinariosComponent = <EventosOrdinariosPage
        idCaracteristica={idCaracteristica}
        producao={eventosProducao}
        setProducao={(value) => {
            setEventosProducao(value)
            setCadastroProducao(value)
        }}
        loading={loading}
        setLoading={loadingCallbacks.ordinarios}
        setSourceLoading={setSourceLoading}
        cetip={cetip}
        jurosOptions={jurosOptions}
        versaoDocumento={versaoDocumento}
        caracteristica={caracteristica}
        eventosProjetados={eventosProjetados}
        datasAniversarioProjetadas={datasAniversarioProjetadas}
        recompraProgramadaProjetados={recompraProgramadaProjetados}
        onDraftChange={updateValidationDraft}
        onDraftEdit={markValidationDraftEdited}
    />

    const eventosExtraordinariosComponent = <EventosExtraordinariosPage
        idCaracteristica={idCaracteristica}
        cetip={cetip}
        eventosProducao={eventosExtraordinariosProducao}
        setEventosProducao={setEventosExtraordinariosProducao}
        versaoDocumento={versaoDocumento}
        caracteristica={caracteristica}
        onDraftChange={updateValidationDraft}
        onDraftEdit={markValidationDraftEdited}
        onDraftLoadingChange={loadingCallbacks.extraordinarios}
    />

    const defineHeader = useCallback((Status, CETIP, VersaoDocumento) => {
        const isCadastroEmProducao = Status === "Em Produção"
        const status = isCadastroEmProducao ? "Em Produção" : "Em Andamento"

        setParametrosProducao(isCadastroEmProducao)
        setHeader({
            title: `${CETIP} | VER ${VersaoDocumento}`,
            statusLabel: status.toUpperCase(),
            statusColor: "warning",
            statusIcon: isCadastroEmProducao ? <CheckIcon /> : <WarningIcon />,
        })
    }, [])

    const handleExportExcel = useCallback(async (payload) => {
        try {
            const {exported, hasEmptySheets} = await exportExcel(
                payload,
                VALIDATION_MEMORY_COLUMNS
            )

            if (!exported) {
                dispatchSnackbar(
                    "Não há dados para exportar.",
                    "warning"
                )
                return
            }

            dispatchSnackbar(
                hasEmptySheets
                    ? "Download do arquivo iniciado. Algumas abas não possuem dados para exportar."
                    : "Download do arquivo iniciado.",
                "success"
            )
        } catch (error) {
            console.error("Erro ao exportar Excel:", error)
            dispatchSnackbar("Erro ao exportar Excel.", "error")
        }
    }, [exportExcel])


    const tabsData = [
        {
            value: MODULOS_CADASTRO_EVENTOS.PARAMETROS,
            label: 'PARÂMETROS',
            content: parametrosComponent
        },
        {
            value: MODULOS_CADASTRO_EVENTOS.EVENTOS_ORDINARIOS,
            label: 'EVENTOS ORDINÁRIOS',
            content: eventosOrdinariosComponent
        },
        {
            value: MODULOS_CADASTRO_EVENTOS.EVENTOS_EXTRAORDINARIOS,
            label: 'EVENTOS EXTRAORDINÁRIOS',
            content: eventosExtraordinariosComponent
        },
        {
            value: MODULOS_CADASTRO_EVENTOS.VALIDACAO_TAB,
            label: 'VALIDAÇÃO',
            content: (
                <ValidationPage
                    onCalculate={readyToCalculate ? handleCalculate : undefined}
                    onBack={() => navigate(-1)}
                    parameters={validationParameters}
                    onParametersChange={setValidationParameters}
                    savedViewModel={savedViewModel}
                    isLoadingValidation={isLoadingValidation}
                    referenceDateError={referenceDateError}
                    validationSnapshot={validationSnapshot}
                    validationContext={{
                        cetip,
                        versaoDocumento,
                        idCaracteristica,
                        caracteristica,
                    }}
                    onExport={handleExportExcel}
                    enableEmissionSave={readyToCalculate}
                    onSaveEmission={saveEmission}
                    isSavingEmission={isSavingEmission}
                    onSaveValidation={readyToCalculate ? saveValidation : undefined}
                    isSavingValidation={isSavingValidation}
                    calculationStatus={validationState.calculation.identity === calculationIdentity
                        ? validationState.calculation.status
                        : "idle"}
                    viewModel={validationState.calculation.viewModel}
                />
            )
        },
    ]

    useEffect(() => {
        if (!hasCadastroContext) {
            if (isInitialMount.current) {
                dispatchSnackbar("Selecione um cadastro para visualizar os eventos.", "warning")
                navigate(MOTOR_EVENTOS_PAGE, { replace: true })
            }
            return undefined
        }

        isInitialMount.current = false
        let isCurrent = true

        defineHeader(state.Status, cetip, versaoDocumento)
        loadCaracteristicas(idCaracteristica, calculationIdentity, () => isCurrent)

        return () => {
            isCurrent = false
        }
    }, [calculationIdentity, cetip, defineHeader, hasCadastroContext, idCaracteristica, loadCaracteristicas, navigate, state, versaoDocumento])

    useEffect(() => {
        if (!hasCadastroContext) return

        setTabValue(initialTab)
        setEventosProducao(false)
        setEventosExtraordinariosProducao(false)
        setEventosProjetados(null)
        setRecompraProgramadaProjetados(null)
        setDatasAniversarioProjetadas(null)
        dispatchValidationState({type: "RESET"})
    }, [cadastroId, state?.CETIP, state?.VersaoDocumento, hasCadastroContext, initialTab])

    useEffect(() => {
        if (!hasCadastroContext) return

        loadJuros()
    }, [hasCadastroContext, loadJuros])

    if (!hasCadastroContext) return null

    return (
        <SideBarLayout
            title={pageTitle}
            loading={loading}
            header={true}
            headerTitle=""
            headerImage={"/background/eventos_headers.jpg"}
            headerPosition={"1% 41%"}
        >
            <Box
                sx={{
                    marginTop: -14.5,
                    paddingBottom: 5,
                    display: "flex",
                    flexDirection: "column",
                    gap: 3,
                    px: { xxxs: 3, md: 3, lg: 0 },
                }}
            >
                <Box mb={4}>
                    <FormHeader
                        title={header.title}
                        statusLabel={header.statusLabel}
                        statusColor={header.statusColor}
                        statusIcon={header.statusIcon}
                    />
                </Box>
            </Box>
            <Box sx={{ mt: -9, position: 'relative', zIndex: 2 }}>
                <Box sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                }}>
                    <TabsWrappedLabel
                        key={cadastroId}
                        value={tabValue}
                        onChange={(_, value) => setTabValue(value)}
                        tabs={tabsData} noReload={true} />
                </Box>
            </Box>
        </SideBarLayout>
    )
}

export default MotorCadastroEventosPage
